// Application Constants

// Widget
export const WIDGET_CONSTANTS = {
  DATA_ATTRIBUTES: {
    CHATBOT_ID: 'data-sidd-chatbot-id',
    USER_ID: 'data-user-id',
    AUTO_OPEN: 'data-auto-open',
    POSITION: 'data-position',
    THEME: 'data-theme',
    INITIAL_MESSAGE: 'data-initial-message',
    CHAT_MESSAGES: 'data-chat-messages',
  },
  CONTAINER_ID: 'sidd-chatbot-widget',
  CSS_CLASSES: {
    CHATBOT: 'siddchatbot',
    WIDGET: 'siddchatbot-widget',
  },
  POSITIONS: {
    BOTTOM_RIGHT: 'bottom-right',
    BOTTOM_LEFT: 'bottom-left',
    TOP_RIGHT: 'top-right',
    TOP_LEFT: 'top-left',
    FILL_PARENT: 'fill-parent',
  } as const,
  // Mapping from backend Position enum to CSS classes
  POSITION_MAPPING: {
    BottomRight: 'bottom-right',
    BottomLeft: 'bottom-left',
    TopRight: 'top-right',
    TopLeft: 'top-left',
    FillParent: 'fill-parent',
  } as const,
  DEFAULT_POSITION: 'bottom-right' as const,
  DEFAULT_THEME: 'auto' as const,
} as const

// API Configuration
// Note: VITE_API_BASE_URL should be set in each app's .env.local file
// The defaults here are fallbacks used during library build
export const API_CONFIG = {
  // Default to localhost for development - apps should override via VITE_API_BASE_URL
  DEV_BASE_URL: 'http://localhost:5187/chatbot',
  // Production URL - apps should override via VITE_API_BASE_URL
  PROD_BASE_URL: 'https://api.laivly.cloud/chatbot',
  TIMEOUT: 60000,
  RETRIES: 3,
  RETRY_DELAY: 1000,
} as const

// Storage Keys
export const STORAGE_KEYS = {
  AUTH: {
    TOKEN: 'sidd_chat_token',
    USER: 'sidd_chat_user',
    USER_PROFILE: 'sidd_chat_user_profile',
    TENANT_ID: 'sidd_chat_tenant_id',
  },
  CHAT: {
    MESSAGES: 'sidd_chat_messages',
    SESSION: 'sidd_chat_session',
    PREFERENCES: 'sidd_chat_preferences',
  },
  CONFIG: {
    THEME: 'sidd_chat_theme',
    LANGUAGE: 'sidd_chat_language',
    DATA_MODE: 'chatbot_data_mode',
  },
} as const

// Theme
export const THEME_CONSTANTS = {
  /** Raw HSL values for contrast text on the primary brand color */
  CONTRAST_COLORS: {
    LIGHT: '0 0% 100%',
    DARK: '0 0% 9%',
  },
  CSS_VARIABLES: {
    PRIMARY: '--primary',
    PRIMARY_CONTRAST: '--primary-contrast',
  },
} as const

export const STYLE_CONSTANTS = {
  CSS_VARIABLES: {
    ZOOM_FACTOR: '--siddchatbot-zoom-factor',
  },
} as const

// Chatbot Config Defaults
export const CHATBOT_CONSTANTS = {
  NAME: 'Chat Assistant',
  DESCRIPTION: 'AI-powered chat assistant',
  BRAND_COLOR: '#3b82f6',
  IS_ACTIVE: true,
  SHOW_SOURCE_ATTRIBUTION: false,
  WELCOME: {
    TITLE: 'Ask Sidd',
    SUBTITLE: 'Got questions? Sidd has the answers.',
  },
  LIMITS: {
    MAX_DESCRIPTION_LENGTH: 500,
    MAX_NAME_LENGTH: 100,
    MIN_NAME_LENGTH: 1,
  },
  PATTERNS: {
    HEX_COLOR: /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
    URL: /^https?:\/\/.+/,
    GUID: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  },
} as const

// Auth
export const AUTH_CONSTANTS = {
  STATES: {
    ANONYMOUS: 'anonymous',
    AUTHENTICATED: 'authenticated',
    EXPIRED: 'expired',
    INVALID: 'invalid',
  } as const,
  DEFAULT_TENANT_ID: '',
  VALIDATION_INTERVAL: 5 * 60 * 1000,
} as const

export const CALLBACK_CONSTANTS = {
  PROPERTY_NAMES: {
    ON_BACK_CLICK: 'onBackClick',
    ON_SOURCE_CLICK: 'onSourceClick',
    ANALYTICS_CALLBACK: 'analyticsCallback',
  } as const,
  ALLOWED_FUNCTIONS: ['handleBackClick', 'handleAnalytics'] as const,
} as const

export const MESSAGE_CONSTANTS = {
  STATUS: {
    SENDING: 'sending',
    SENT: 'sent',
    FAILED: 'failed',
  } as const,
  ROLE: {
    USER: 'user',
    ASSISTANT: 'assistant',
  } as const,
  MAX_CONVERSATION_TURNS: 3,
  MESSAGES_PER_TURN: 2,
} as const

export const SESSION_CONSTANTS = {
  STATUS: {
    ACTIVE: 'active',
    ENDED: 'ended',
    EXPIRED: 'expired',
  },
} as const

export const SESSION_ERROR_MESSAGES = {
  SESSION_NOT_ACTIVE: 'Chat session is not active',
  SESSION_NOT_FOUND: 'Chat session not found',
} as const

export const FEEDBACK_CONFIG = {
  CATEGORIES: [
    { label: 'Incorrect or unhelpful response', apiCategory: 'Negative' },
    { label: 'Slow or not responding', apiCategory: 'Issue' },
    { label: 'Display or formatting issue', apiCategory: 'Issue' },
    { label: 'Suggestion or feature request', apiCategory: 'Suggestion' },
    { label: 'Other', apiCategory: 'Issue' },
  ],
} as const
